/**
 * Widget Carrousel - Affiche les offres touristiques sous forme de carrousel
 */

import { html, unsafeCSS, CSSResultGroup } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { customElement, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";
import { BaseWidget } from "../components/BaseWidget";
import "../components/WidgetLayout";
import "../components/Icon";
import "../components/ListItemCard";
import { ObjetTouristique } from "../types/apidae.d";
import { getFieldData, getFieldIconName } from "../utils/itemFieldUtils";
import {
  getOpenSystemWidgetFromItem,
  isOpenSystemConfigured,
  getEllohaWidgetFromItem,
  isEllohaConfigured,
} from "../utils/displayedFieldUtils";
import { getPriceFromBatch } from "../services/EllohaService";
import { DisplayedFieldId } from "../types/widgets.d";
import carouselWidgetStyles from "../styles/carousel-widget.css?inline";
import listWidgetStyles from "../styles/list-widget.css?inline";
import arrowLeftIcon from "../assets/icons/bold/arrows/round_alt_arrow_left.svg?raw";
import arrowRightIcon from "../assets/icons/bold/arrows/round_alt_arrow_right.svg?raw";
import {
  formatDate,
  getEllohaCultureFromLanguage,
  getImageUrl,
  getLabel,
  t,
} from "../utils/widgetsUtils";

@customElement("apidae-widget-carrousel")
export class CarouselWidget extends BaseWidget {
  static styles: CSSResultGroup = [
    BaseWidget.sharedStyles,
    unsafeCSS(listWidgetStyles),
    unsafeCSS(carouselWidgetStyles),
  ];

  @state()
  private currentSlide: number = 0;

  @state()
  private itemsToShow: 1 | 3 | 5 = 1;

  @state()
  private failedImages: Set<number> = new Set();

  @state()
  private loadedImages: Set<number> = new Set();

  @state()
  private ellohaPrices: Map<string, number> = new Map();

  @state()
  private translateXPixels: number = 0;

  private trackRef: Ref<HTMLDivElement> = createRef();
  private containerRef: Ref<HTMLDivElement> = createRef();
  private configuredItemsToShow: 1 | 3 | 5 = 1;
  private hasCalculatedAdaptiveItems: boolean = false;

  firstUpdated() {
    // Attendre que le DOM soit complètement rendu avant de calculer
    // Utiliser plusieurs requestAnimationFrame pour s'assurer que les dimensions sont disponibles
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateTranslation();
        this.calculateAdaptiveItemsToShow();
      });
    });
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has("config") && this.config) {
      const carouselConfig = this.config.templateSpecificConfig?.carousel;
      if (carouselConfig?.nbItemsToShow) {
        // S'assurer que nbItemsToShow est 1, 3 ou 5
        const nbItems = carouselConfig.nbItemsToShow;
        this.configuredItemsToShow =
          nbItems === 1 || nbItems === 3 || nbItems === 5 ? nbItems : 1;
      } else {
        // Valeur par défaut
        this.configuredItemsToShow = 1;
      }

      // Calculer la valeur adaptative uniquement à l'initialisation
      if (!this.hasCalculatedAdaptiveItems) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.calculateAdaptiveItemsToShow();
          });
        });
      }
    }

    if (changedProperties.has("items")) {
      this.loadEllohaPrices();
      if (this.items.length > 0) {
        // Réinitialiser le slide si nécessaire
        if (this.currentSlide >= this.maxSlideIndex) {
          this.currentSlide = 0;
        }
      }
    }

    // Recalculer la translation après le rendu
    if (
      changedProperties.has("currentSlide") ||
      changedProperties.has("items") ||
      changedProperties.has("itemsToShow")
    ) {
      requestAnimationFrame(() => {
        this.updateTranslation();
      });
    }
  }

  private async loadEllohaPrices() {
    const displayedFields = this.config?.contentCustomization?.displayedFields;
    if (!isEllohaConfigured(displayedFields) || !this.items?.length) {
      this.ellohaPrices = new Map();
      return;
    }
    const field = displayedFields?.find(
      (f) => f.id === DisplayedFieldId.DISPONIBILITES_ELLOHA,
    );
    const data = field?.data as { idBookingEngine?: string } | undefined;
    const idBookingEngine = data?.idBookingEngine;
    if (!idBookingEngine) return;

    const idProducts: string[] = [];
    for (const item of this.items) {
      const elloha = getEllohaWidgetFromItem(item);
      if (elloha?.idProduct) idProducts.push(elloha.idProduct);
    }
    if (idProducts.length === 0) {
      this.ellohaPrices = new Map();
      return;
    }

    const culture = getEllohaCultureFromLanguage(this.language);
    const map = await getPriceFromBatch(idBookingEngine, idProducts, culture);
    this.ellohaPrices = new Map(map);
    this.requestUpdate();
  }

  /**
   * Calcule le nombre d'items à afficher en fonction de l'espace disponible
   * Descend au palier précédent si l'espace n'est pas suffisant
   * Ne s'exécute qu'à l'initialisation
   */
  private calculateAdaptiveItemsToShow(): void {
    // Ne calculer qu'une seule fois à l'initialisation
    if (this.hasCalculatedAdaptiveItems) {
      return;
    }

    if (!this.containerRef.value) {
      // Réessayer plus tard si le conteneur n'est pas encore disponible
      setTimeout(() => {
        if (!this.hasCalculatedAdaptiveItems) {
          this.calculateAdaptiveItemsToShow();
        }
      }, 100);
      return;
    }

    const containerWidth =
      this.containerRef.value.getBoundingClientRect().width;

    // Si la largeur n'est pas encore disponible, réessayer
    if (containerWidth === 0 || !containerWidth) {
      setTimeout(() => {
        if (!this.hasCalculatedAdaptiveItems) {
          this.calculateAdaptiveItemsToShow();
        }
      }, 100);
      return;
    }

    // Padding gauche + droite = 40px * 2 = 80px
    const availableWidth = containerWidth - 80;

    // Largeur d'une card = 256px, gap entre cards = 20px
    const cardWidth = 256;
    const gapBetweenCards = 20;

    // Les paliers possibles : 1, 3, 5
    const tiers = [1, 3, 5] as const;

    // Calculer combien d'items peuvent tenir dans l'espace disponible
    let maxItemsThatFit = 0;
    for (const tier of tiers) {
      // Largeur nécessaire = nbItems * cardWidth + (nbItems - 1) * gap
      const requiredWidth = tier * cardWidth + (tier - 1) * gapBetweenCards;
      if (requiredWidth <= availableWidth) {
        maxItemsThatFit = tier;
      } else {
        break;
      }
    }

    // Si aucun palier ne peut tenir, on prend le minimum (1)
    if (maxItemsThatFit === 0) {
      maxItemsThatFit = 1;
    }

    // Choisir le palier le plus grand mais <= maxItemsThatFit et <= configuredItemsToShow
    let adaptiveItemsToShow: 1 | 3 | 5 = 1;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (
        tiers[i] <= maxItemsThatFit &&
        tiers[i] <= this.configuredItemsToShow
      ) {
        adaptiveItemsToShow = tiers[i];
        break;
      }
    }

    // Marquer comme calculé et mettre à jour
    this.hasCalculatedAdaptiveItems = true;
    this.itemsToShow = adaptiveItemsToShow;
    this.setAttribute("items-to-show", this.itemsToShow.toString());

    // Réinitialiser le slide si nécessaire
    if (this.currentSlide >= this.maxSlideIndex) {
      this.currentSlide = 0;
    }

    this.requestUpdate();
  }

  private updateTranslation() {
    if (!this.trackRef.value || this.items.length === 0) {
      this.translateXPixels = 0;
      return;
    }

    const firstGroup = this.trackRef.value.querySelector(
      ".carousel-group",
    ) as HTMLElement;
    if (firstGroup) {
      const groupRect = firstGroup.getBoundingClientRect();
      const groupWidth = groupRect.width;
      const gap = 20; // gap entre les groupes

      // Pour déplacer d'un groupe, on doit déplacer de la largeur d'un groupe + gap
      const step = groupWidth + gap;
      this.translateXPixels = -(this.currentSlide * step);
      this.requestUpdate();
    }
  }

  private get maxSlideIndex(): number {
    if (this.items.length === 0) return 0;
    // Le nombre de "pages" est le nombre total d'items divisé par itemsToShow, arrondi à l'entier supérieur
    // Moins 1 car on commence à l'index 0
    return Math.ceil(this.items.length / this.itemsToShow) - 1;
  }

  /**
   * Vérifie si les flèches sont actives (peuvent avoir un effet)
   * Les flèches sont actives si on peut naviguer :
   * - Vers d'autres groupes dans les résultats actuels, OU
   * - Vers d'autres pages (suivante ou précédente)
   */
  private get areArrowsActive(): boolean {
    if (this.items.length === 0) return false;
    const totalGroups = Math.ceil(this.items.length / this.itemsToShow);
    // Si on a plus d'un groupe, on peut naviguer entre les groupes
    if (totalGroups > 1) return true;
    // Si on a qu'un seul groupe, on peut naviguer si :
    // - On peut charger plus de résultats
    // - OU on n'est pas sur la première page (on peut charger la page précédente)
    const itemsLoaded =
      (this.currentPage - 1) * this.itemsPerPage + this.items.length;
    const canLoadMore = this.hasMore || this.totalItems > itemsLoaded;
    return canLoadMore || this.currentPage > 1;
  }

  /**
   * Charge une page de données et remplace les items existants (pour le carousel)
   * @param page Numéro de la page à charger
   * @param showLoading Afficher l'écran de chargement (par défaut false pour ne pas cacher le contenu)
   */
  private async loadDataAndReplace(
    page: number,
    showLoading: boolean = false,
  ): Promise<void> {
    if (!this.config) {
      return;
    }

    try {
      if (showLoading) {
        this.loading = true;
      }
      this.error = null;

      if (this.useMock) {
        // Simuler un délai de chargement
        await new Promise((resolve) => setTimeout(resolve, 500));
        // Utiliser la méthode de BaseWidget pour charger les données mock
        this.loadMockDataPageAndReplace(page);
      } else {
        // Charger depuis l'API avec pagination
        const result = await this.widgetService.getWidgetData(
          this.modePreview,
          this.widgetId,
          this.config,
          page,
          this.itemsPerPage,
          this.dateRange.dateDebut || undefined,
          this.dateRange.dateFin || undefined,
          undefined, // identifiants
          undefined, // center
          undefined, // radius
          undefined, // filters
        );

        // Toujours remplacer les items pour le carousel
        this.items = result.items;
        this.totalItems = result.total;
        this.hasMore = result.hasMore;
        this.currentPage = page;
      }

      if (showLoading) {
        this.loading = false;
      }
    } catch (err) {
      console.error("Erreur lors du chargement des données:", err);
      this.error =
        err instanceof Error ? err.message : "Erreur de chargement des données";
      if (page === 1) {
        this.items = [];
        this.totalItems = 0;
        this.hasMore = false;
      }
      if (showLoading) {
        this.loading = false;
      }
    }
  }

  /**
   * Calcule le numéro de la dernière page disponible
   */
  private get lastPageNumber(): number {
    if (this.totalItems === 0) return 1;
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  private async nextSlide() {
    if (this.items.length === 0) return;

    const isLastSlide = this.currentSlide >= this.maxSlideIndex;

    // Vérifier si on peut charger plus en comparant le nombre total d'items avec ce qu'on a déjà chargé
    // On a chargé (currentPage - 1) * itemsPerPage + filteredItems.length items
    const itemsLoaded =
      (this.currentPage - 1) * this.itemsPerPage + this.items.length;
    const canLoadMore = this.hasMore || this.totalItems > itemsLoaded;

    if (isLastSlide && canLoadMore) {
      // On est au dernier groupe et on peut charger plus : charger la page suivante
      await this.loadDataAndReplace(this.currentPage + 1);
      // Après le chargement, on va au premier groupe (index 0)
      this.currentSlide = 0;
    } else if (isLastSlide && !canLoadMore) {
      // On est au dernier groupe et on ne peut pas charger plus : recharger la première page pour boucler
      await this.loadDataAndReplace(1);
      this.currentSlide = 0;
    } else {
      // On n'est pas au dernier groupe : aller au suivant
      this.currentSlide++;
    }

    this.requestUpdate();
  }

  private async prevSlide() {
    if (this.items.length === 0) return;

    if (this.currentSlide === 0) {
      // On est au premier groupe
      if (this.currentPage > 1) {
        // On peut charger la page précédente
        await this.loadDataAndReplace(this.currentPage - 1);
        // Après le chargement, on va au dernier groupe de la nouvelle page
        this.currentSlide = this.maxSlideIndex;
      } else if (this.currentPage === 1 && this.lastPageNumber > 1) {
        // On est sur la première page mais il y a d'autres pages : charger la dernière page
        await this.loadDataAndReplace(this.lastPageNumber);
        // Après le chargement, on va au dernier groupe de la dernière page
        this.currentSlide = this.maxSlideIndex;
      } else {
        // On ne peut pas charger d'autre page : boucler à la fin
        this.currentSlide = this.maxSlideIndex;
      }
    } else {
      // Aller au groupe précédent
      this.currentSlide--;
    }

    this.requestUpdate();
  }

  private goToSlide(pageIndex: number) {
    this.currentSlide = Math.max(0, Math.min(pageIndex, this.maxSlideIndex));
    this.requestUpdate();
  }

  protected renderWidget() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="loading-spinner"></div>
          <p>${t("loading", this.language)}</p>
        </div>
      `;
    }

    if (this.error) {
      return this.renderErrorScreen();
    }

    // La translation est calculée dans updateTranslation() après le rendu

    const visual = this.config?.visualCustomization ?? null;
    return html`
      <apidae-widget-layout
        .config=${{
          fraction: "full",
          showFilters: false,
          contentSlot: "content",
        }}
        .visualCustomization=${visual}
      >
        <div slot="content" class="carousel-wrapper">
          ${this.items.length === 0
            ? html`
                <div class="empty-state">
                  <div class="empty-state-title">
                    ${t("noResultsFound", this.language) ||
                    "Aucun résultat trouvé."}
                  </div>
                  <div class="empty-state-subtitle">
                    ${t("adjustSearchWithFilters", this.language) ||
                    "Ajustez votre recherche grâce aux filtres."}
                  </div>
                </div>
              `
            : html`
                <div class="carousel-container" ${ref(this.containerRef)}>
                  <div
                    class="carousel-track"
                    ${ref(this.trackRef)}
                    style="transform: translateX(${this.translateXPixels}px)"
                  >
                    ${this.renderItemGroups()}
                  </div>

                  ${this.items.length > 0
                    ? html`
                        <button
                          class="carousel-nav carousel-nav-prev ${!this
                            .areArrowsActive
                            ? "inactive"
                            : ""}"
                          @click=${this.prevSlide}
                          aria-label=${t("previous", this.language) ||
                          "Précédent"}
                          ?disabled=${!this.areArrowsActive}
                        >
                          ${unsafeHTML(
                            arrowLeftIcon.replace(
                              'fill="#2F2F2F"',
                              'fill="currentColor"',
                            ),
                          )}
                        </button>
                        <button
                          class="carousel-nav carousel-nav-next ${!this
                            .areArrowsActive
                            ? "inactive"
                            : ""}"
                          @click=${this.nextSlide}
                          aria-label=${t("next", this.language) || "Suivant"}
                          ?disabled=${!this.areArrowsActive}
                        >
                          ${unsafeHTML(
                            arrowRightIcon.replace(
                              'fill="#2F2F2F"',
                              'fill="currentColor"',
                            ),
                          )}
                        </button>
                      `
                    : ""}
                </div>

                ${this.items.length > 0 ? this.renderDots() : ""}
              `}
        </div>
      </apidae-widget-layout>
    `;
  }

  private renderItemGroups() {
    const groups: ObjetTouristique[][] = [];
    for (let i = 0; i < this.items.length; i += this.itemsToShow) {
      groups.push(this.items.slice(i, i + this.itemsToShow));
    }

    return groups.map(
      (group) => html`
        <div class="carousel-group">
          ${group.map((item) => this.renderListItem(item))}
        </div>
      `,
    );
  }

  private renderListItem(item: ObjetTouristique) {
    const imageUrl = getImageUrl(item, "diaporama", this.language);
    const nom = getLabel(item.nom, this.language);
    const displayedFields =
      this.config?.contentCustomization?.displayedFieldsPreview || [];
    const hasOpenSystem =
      isOpenSystemConfigured(
        this.config?.contentCustomization?.displayedFields,
      ) && !!getOpenSystemWidgetFromItem(item)?.ui;
    const ellohaFromItem = getEllohaWidgetFromItem(item);
    const hasElloha =
      isEllohaConfigured(this.config?.contentCustomization?.displayedFields) &&
      !!ellohaFromItem?.idProduct;
    const ellohaPriceFrom =
      hasElloha && ellohaFromItem?.idProduct
        ? this.ellohaPrices.get(ellohaFromItem.idProduct) ?? null
        : null;

    return html`
      <apidae-list-item-card
        .item=${item}
        .imageUrl=${imageUrl}
        .nom=${nom}
        .displayedFields=${displayedFields}
        .hasOpenSystem=${hasOpenSystem}
        .hasElloha=${hasElloha}
        .ellohaPriceFrom=${ellohaPriceFrom}
        .typography=${this.config?.visualCustomization?.typography}
        .language=${this.language}
        .apiFilters=${this.apiFilters}
        .failedImages=${this.failedImages}
        .loadedImages=${this.loadedImages}
        .t=${(key: string) => t(key, this.language)}
        .getFieldData=${(
          it: ObjetTouristique,
          fieldId: string,
          _apiFilters: unknown,
          _language: string,
        ) => this.getFieldData(it, fieldId)}
        .getFieldIconName=${getFieldIconName}
        .onItemClick=${(clickedItem: ObjetTouristique) =>
          this.handleItemClick(clickedItem)}
        .onImageLoad=${(id: number) => {
          this.loadedImages.add(id);
          this.requestUpdate();
        }}
        .onImageError=${(id: number) => {
          this.failedImages.add(id);
          this.requestUpdate();
        }}
        .visualCustomization=${this.config?.visualCustomization ?? null}
      ></apidae-list-item-card>
    `;
  }

  private getFieldData(
    item: ObjetTouristique,
    fieldId: string,
  ): { value: string; url?: string } | null {
    return getFieldData(item, fieldId, {
      language: this.language,
      getLabel: (libelle) => getLabel(libelle, this.language),
      formatDate: (dateStr, options) =>
        formatDate(dateStr, this.language, options),
      t: (key) => t(key, this.language),
    });
  }

  private renderDots() {
    const totalDots = Math.ceil(this.items.length / this.itemsToShow);
    const dots = [];

    for (let i = 0; i < totalDots; i++) {
      const isActive = this.currentSlide === i;
      dots.push(html`
        <button
          class="carousel-dot ${isActive ? "active" : ""}"
          @click=${() => this.goToSlide(i)}
          aria-label="Aller à la page ${i + 1}"
        ></button>
      `);
    }

    return html`<div class="carousel-dots">${dots}</div>`;
  }

  render() {
    return html`
      <div class="widget-container">
        <div class="widget-content">${this.renderWidget()}</div>
        ${this.renderDetailView()} ${this.renderToast()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-widget-carrousel": CarouselWidget;
  }
}
